import { Anchor, Box, Paragraph, TextButton } from '@zillow/constellation-10';
import { AttachmentCard } from '@zg-rentals/satellite-components';
import { useAuth } from '@zg-rentals/auth-comet';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata } from '@zg-rentals/ts-satellite-client-react-query';
import { ModalType } from '../../../../types/ModalType';
import {
  ATTACHMENT_SIDEBAR_DOWNLOAD_CLICK_EVENT_TYPE_ID,
  ATTACHMENT_SIDEBAR_PREVIEW_CLICK_EVENT_TYPE_ID,
  emitAttachmentSidebarDownloadClickEvent,
  emitAttachmentSidebarPreviewClickEvent,
} from '../../../../constants/clickstreamEvents/actionSidebar';
import useModal from '../../../../hooks/useModal';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import InboxSidebarSection from '../InboxSidebarSection';
import { logAttachmentsRenderError } from '../../../MessageAttachments';
import { mapAttachmentMetadataDtoToAttachment } from '../../../../utils/attachmentMetadataUtils';

const LOGGER_NAME = 'InboxSidebar_Attachments';
const MAX_DISPLAYED_ATTACHMENTS = 3;

export default function Attachments() {
  const { setActiveModal } = useModal();
  const [shouldLogAttachmentsRenderError, setShouldLogAttachmentsRenderError] = useState(false);

  const { conversationId = '' } = useContext(InboxPageContext);
  const { user } = useAuth();
  const { data: attachmentsData } = useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata(
    { conversationId },
    {
      enabled: !!conversationId,
      meta: {
        context: {
          api: 'getAttachmentsMetadata',
          details: `${LOGGER_NAME} - Error fetching attachments`,
          conversationId,
        },
      },
    },
  );

  const attachments = useMemo(
    () => (attachmentsData?.attachmentMetadataDtos ?? []).map(mapAttachmentMetadataDtoToAttachment),
    [attachmentsData?.attachmentMetadataDtos],
  );

  const [displayAll, setDisplayAll] = useState(false);

  useEffect(() => {
    if (shouldLogAttachmentsRenderError) {
      logAttachmentsRenderError({ conversationId, loggerName: LOGGER_NAME });
    }
  }, [shouldLogAttachmentsRenderError, conversationId]);

  if (!attachments.length) {
    return null;
  }

  const filteredAttachments = attachments.filter((attachment) => !attachment.isAttachmentDeleted);
  const attachmentsForDisplay = displayAll
    ? filteredAttachments
    : filteredAttachments.slice(0, MAX_DISPLAYED_ATTACHMENTS);
  const shouldShowExpandButton = filteredAttachments.length > MAX_DISPLAYED_ATTACHMENTS;

  const handleShowAttachmentModal = (): void => {
    setActiveModal(ModalType.AttachmentModal);
  };

  const handleEventTracking = (isImageFileType = false) => {
    if (isImageFileType) {
      emitAttachmentSidebarPreviewClickEvent(user);
    } else {
      emitAttachmentSidebarDownloadClickEvent(user);
    }
  };

  return (
    <InboxSidebarSection title="Attachments">
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
        <Paragraph textStyle="body-sm">
          Attachments will be deleted 30 days after sending or receiving them on the platform.&nbsp;
          <Anchor asChild>
            <TextButton textStyle="body-sm-bold" onClick={handleShowAttachmentModal}>
              Read guidelines
            </TextButton>
          </Anchor>
        </Paragraph>

        {attachmentsForDisplay.map((attachment) => (
          <AttachmentCard
            key={
              attachment.zDocId || `${attachment.fileName}-${attachment.fileSizeBytes}-${attachment.attachmentAddedMs}`
            }
            attachment={attachment}
            previewEventId={ATTACHMENT_SIDEBAR_PREVIEW_CLICK_EVENT_TYPE_ID}
            downloadEventId={ATTACHMENT_SIDEBAR_DOWNLOAD_CLICK_EVENT_TYPE_ID}
            onEventTracking={handleEventTracking}
            onLogError={() => setShouldLogAttachmentsRenderError(true)}
          />
        ))}

        {shouldShowExpandButton && (
          <TextButton
            onClick={() => setDisplayAll(!displayAll)}
            textStyle="body-sm-bold"
            css={{ justifyContent: 'left' }}
          >
            {displayAll ? 'Show less' : 'Show all attachments'}
          </TextButton>
        )}
      </Box>
    </InboxSidebarSection>
  );
}
